<template>
  <div :data-testid="rootTestId" class="cycle-team-members-table">
    <lb-datatableaction
      ref="dataTableAction"
      :headers="headers"
      :tabledata="resolvedSourceItems"
      :displayed-row-count="items.length"
      :itemsperpage="itemsPerPage"
      :bulk-actions="false"
      :show-toolbar="showToolbar"
      :selectfilter="selectfilter"
      :auto-height="autoHeight"
      :data-testid="`${dataTestidPrefix}__datatable`"
      @filter-item="$emit('filter-item', $event)"
      @clear-item="$emit('clear-item')"
      @filter-active-change="$emit('filter-active-change', $event)"
    >
      <template v-slot:tbody>
        <tr
          v-for="(item, index) in items"
          :key="item._id || index"
          class="cycle-team-members-table__row"
          :data-testid="`${dataTestidPrefix}__row--${index}`"
        >
          <template v-for="header in headers">
            <td
              v-if="header.visible && isMemberColumn(header.value)"
              :key="`member-${header.value}`"
              class="px-4 cycle-team-members-table__td cycle-team-members-table__td--member"
              :class="header.cellClass"
              :data-testid="`${dataTestidPrefix}__member--${index}`"
            >
              <div class="cycle-team-members-table__member">
                <div
                  class="cycle-team-members-table__avatar"
                  :data-testid="`${dataTestidPrefix}__avatar--${index}`"
                >
                  {{ getMemberInitials(item.name) }}
                </div>
                <div class="cycle-team-members-table__member-text">
                  <span
                    class="cycle-team-members-table__name"
                    :data-testid="`${dataTestidPrefix}__name--${index}`"
                  >
                    <template v-if="isInactiveName(item.name)">
                      {{ displayMemberName(item.name) }}
                      <span class="cycle-team-members-table__name-inactive"> (Inactive)</span>
                    </template>
                    <template v-else>{{ displayMemberName(item.name) }}</template>
                  </span>
                  <span
                    class="cycle-team-members-table__email"
                    :data-testid="`${dataTestidPrefix}__email--${index}`"
                  >
                    {{ item.email || '' }}
                  </span>
                </div>
              </div>
            </td>

            <td
              v-else-if="header.visible && header.value === 'action'"
              :key="`action-${header.value}`"
              class="px-4 single-line action-data cycle-team-members-table__td cycle-team-members-table__td--actions"
              :class="header.cellClass"
              :data-testid="`${dataTestidPrefix}__actions--${index}`"
            >
              <slot name="actions" :item="item" :index="index" />
            </td>

            <td
              v-else-if="header.visible"
              :key="header.value"
              class="px-4 single-line cycle-team-members-table__td"
              :class="[{ textLowercase: header.value === 'email' }, header.cellClass]"
              :data-testid="`${dataTestidPrefix}__cell--${header.value}--${index}`"
            >
              <slot
                :name="`cell-${header.value}`"
                :item="item"
                :index="index"
                :header="header"
              >
                {{ getItemData(item, header.value) }}
              </slot>
            </td>
          </template>
        </tr>
      </template>
    </lb-datatableaction>
  </div>
</template>

<script>
export default {
  name: 'CycleTeamMembersTable',
  props: {
    items: { type: Array, default: () => [] },
    /** Unfiltered source rows for DataTableAction filter option lists */
    sourceItems: { type: Array, default: null },
    headers: { type: Array, required: true },
    selectfilter: { type: Object, default: () => ({}) },
    showToolbar: { type: Boolean, default: true },
    itemsPerPage: { type: Number, default: 10 },
    autoHeight: { type: Boolean, default: false },
    dataTestidPrefix: { type: String, default: 'cycle-team-members-table' },
    /** Column value(s) rendered as avatar + name/email member cell */
    memberColumn: { type: [String, Array], default: 'name' }
  },
  computed: {
    rootTestId() {
      return this.dataTestidPrefix;
    },
    resolvedSourceItems() {
      return Array.isArray(this.sourceItems) ? this.sourceItems : this.items;
    }
  },
  methods: {
    isMemberColumn(value) {
      const cols = Array.isArray(this.memberColumn)
        ? this.memberColumn
        : [this.memberColumn];
      return cols.includes(value);
    },
    clearFilters() {
      this.$refs.dataTableAction?.clearFilter?.();
    },
    getItemData(item, key) {
      return item?.[key];
    },
    getMemberInitials(name) {
      const clean = this.displayMemberName(name);
      const parts = clean.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return (clean.slice(0, 2) || '?').toUpperCase();
    },
    displayMemberName(name) {
      return String(name || '')
        .replace(/\s*\(Inactive\)\s*/gi, '')
        .trim();
    },
    isInactiveName(name) {
      return String(name || '').includes('(Inactive)');
    }
  }
};
</script>

<style lang="scss">
@import './CycleTeamMembersTable.scss';
</style>
